import React, { useState, useMemo } from 'react';
import { Card, Row, Col, Button, Form } from 'react-bootstrap';
import { FaArrowLeft, FaCloud, FaLayerGroup, FaMapMarkerAlt } from 'react-icons/fa';
import Select from 'react-select';
import ResourceDetailsTableEnhanced from './ResourceDetailsTableEnhanced';
import RegionMapView from './RegionMapView';
import WorldMapView from './WorldMapView';
import AccountDiagramWidget from './AccountDiagramWidget';

const AccountResourceView = ({ accountId, resources, cloudAccounts, onBack }) => {
  const [typeFilter, setTypeFilter] = useState(null);
  const [regionFilter, setRegionFilter] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'diagram'
  const getAccountAlias = () => {
    const account = cloudAccounts.find(a => a.awsAccountId === accountId);
    return account?.alias || account?.description || accountId;
  };

  const typeOptions = useMemo(() => {
    const types = [...new Set(resources.map(r => r.type))];
    return types.map(type => ({ value: type, label: type }));
  }, [resources]);

  const regionOptions = useMemo(() => {
    const regions = [...new Set(resources.map(r => r.region).filter(Boolean))];
    return regions.map(region => ({ value: region, label: region }));
  }, [resources]);

  const filteredResources = useMemo(() => {
    let filtered = resources;

    if (typeFilter) {
      filtered = filtered.filter(r => r.type === typeFilter.value);
    }

    if (regionFilter) {
      filtered = filtered.filter(r => r.region === regionFilter.value);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r =>
        r.name?.toLowerCase().includes(query) ||
        r.id?.toLowerCase().includes(query) ||
        r.arn?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [resources, typeFilter, regionFilter, searchQuery]);

  const uniqueTypes = useMemo(() => {
    return [...new Set(resources.map(r => r.type))].length;
  }, [resources]);

  const uniqueRegions = useMemo(() => {
    return [...new Set(resources.map(r => r.region).filter(Boolean))].length;
  }, [resources]);

  const handleClearFilters = () => {
    setTypeFilter(null);
    setRegionFilter(null);
    setSearchQuery('');
  };

  return (
    <div className="container">
      {/* Header */}
      <div className="d-flex align-items-center mb-4">
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={onBack}
          style={{ marginRight: '16px' }}
        >
          <FaArrowLeft className="me-2" />
          Back
        </Button>
        <div>
          <h5 className="mb-0" style={{ color: '#1e293b', fontWeight: '600' }}>
            {getAccountAlias()}
          </h5>
          <small style={{ color: '#64748b' }}>Account ID: {accountId}</small>
        </div>
      </div>

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col md={4}>
          <Card style={{
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '12px',
            border: '1px solid #e9ecef',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
          }}>
            <Card.Body className="text-center">
              <div style={{ fontSize: '1.5rem', color: '#6D5BFF', marginBottom: '8px' }}>
                <FaCloud />
              </div>
              <h3 className="mb-0" style={{ color: '#1e293b' }}>{resources.length}</h3>
              <small className="text-muted">Total Resources</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card style={{
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '12px',
            border: '1px solid #e9ecef',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
          }}>
            <Card.Body className="text-center">
              <div style={{ fontSize: '1.5rem', color: '#10b981', marginBottom: '8px' }}>
                <FaLayerGroup />
              </div>
              <h3 className="mb-0" style={{ color: '#1e293b' }}>{uniqueTypes}</h3>
              <small className="text-muted">Resource Types</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card style={{
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '12px',
            border: '1px solid #e9ecef',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
          }}>
            <Card.Body className="text-center">
              <div style={{ fontSize: '1.5rem', color: '#3b82f6', marginBottom: '8px' }}>
                <FaMapMarkerAlt />
              </div>
              <h3 className="mb-0" style={{ color: '#1e293b' }}>{uniqueRegions}</h3>
              <small className="text-muted">Regions</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* World Map View */}
      {viewMode === 'list' && (
        <WorldMapView 
          resources={resources}
          onViewArchitecture={(region) => {
            setSelectedRegion(region);
            setViewMode('diagram');
          }}
        />
      )}

      {/* Architecture Diagram View */}
      {viewMode === 'diagram' && selectedRegion && (
        <Card className="mb-4" style={{
          background: 'white',
          borderRadius: '12px',
          border: '1px solid #e9ecef'
        }}>
          <Card.Body>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h5 className="mb-0" style={{ color: '#1e293b', fontWeight: '600' }}>
                Architecture Diagram - {selectedRegion}
              </h5>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  setSelectedRegion(null);
                  setViewMode('list');
                }}
              >
                ← Back to Map
              </Button>
            </div>
            <AccountDiagramWidget accountId={accountId} region={selectedRegion} />
          </Card.Body>
        </Card>
      )}

      {/* Filter Bar and Table - Only show in list mode */}
      {viewMode === 'list' && (
        <>
          {/* Filter Bar */}
      <Card className="mb-4" style={{
        background: 'rgba(255, 255, 255, 0.95)',
        borderRadius: '12px',
        border: '1px solid #e9ecef'
      }}>
        <Card.Body>
          <Row className="align-items-end">
            <Col md={3}>
              <Form.Label className="small text-muted mb-2">Resource Type</Form.Label>
              <Select
                className="react-select"
                classNamePrefix="react-select"
                options={typeOptions}
                value={typeFilter}
                onChange={setTypeFilter}
                placeholder="All Types"
                isClearable
              />
            </Col>
            <Col md={3}>
              <Form.Label className="small text-muted mb-2">Region</Form.Label>
              <Select
                className="react-select"
                classNamePrefix="react-select"
                options={regionOptions}
                value={regionFilter}
                onChange={setRegionFilter}
                placeholder="All Regions"
                isClearable
              />
            </Col>
            <Col md={4}>
              <Form.Label className="small text-muted mb-2">Search</Form.Label>
              <Form.Control
                type="text"
                placeholder="Search by name, ID, or ARN..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Button
                variant="outline-secondary"
                onClick={handleClearFilters}
                className="w-100"
                disabled={!typeFilter && !regionFilter && !searchQuery}
              >
                Clear Filters
              </Button>
            </Col>
          </Row>
          <div className="mt-3">
            <small className="text-muted">
              Showing <strong>{filteredResources.length}</strong> of <strong>{resources.length}</strong> resources
            </small>
          </div>
        </Card.Body>
      </Card>

      {/* Resource Table */}
      <ResourceDetailsTableEnhanced resources={filteredResources} />
        </>
      )}
    </div>
  );
};

export default AccountResourceView;
